<template>
  <section>
    <div
      v-if="visibleComments.length < totalComments"
      class="tw-mb-4 tw-flex tw-flex-row tw-items-center"
    >
      <button class="normalize-style tw-text-sm tw-text-riser-blue" @click="showMore">
        {{ $t('show-older-comments') }}
      </button>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-6">
      <div v-if="visibleComments.length > 0" class="tw-flex tw-flex-col tw-gap-3">
        <comment
          :isLikeable="props.isLikeable ?? false"
          :reportURL="props.reportURL"
          v-for="comment of visibleComments"
          :key="comment.id"
          :comment="comment"
          :endpoint="props.endpoint"
          @update="updateComments"
        />
      </div>

      <div v-if="!props.hideCommentCreate">
        <form
          v-if="session_token"
          class="tw-flex tw-items-center tw-gap-3"
          @submit.prevent="sendComment"
        >
          <RelationshipUserImage
            thumb
            class="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-rounded-full tw-object-cover"
            :user="currentUser ?? undefined"
          />

          <input
            v-model="state.message"
            class="tw-w-full tw-rounded-lg tw-border-black/10 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-sm"
            :placeholder="$t('Add comment...')"
          />

          <LoadingButton
            :disabled="!state.message"
            :loading="state.loading"
            class="small-button tw-border-none tw-bg-black/10"
            :class="{ 'tw-opacity-50': !state.message }"
          >
            {{ $t('Send') }}
          </LoadingButton>
        </form>

        <div v-else>
          {{ $t('Sign Up or Login to join the conversation') }}
          <br />
          <nuxt-link to="/getstarted" class="button red-button tw-mx-auto tw-my-2">
            {{ $t("Yeah, let's get started!") }}
          </nuxt-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { PostingComment } from '~/types/postings'

const authStore = useAuthStore()

const { createComment, getComments } = useCommentsService()

const props = defineProps<{
  type: string
  comments?: PostingComment[]
  endpoint: string
  reportURL?: string
  isLikeable?: boolean
  hideCommentCreate?: boolean
  visible: number
}>()

const emit = defineEmits(['comment-submit'])

const state = reactive<{
  message: string
  commentsData: PostingComment[] | Ref<PostingComment[]>
  maxVisibleComments: number
  loading: boolean
}>({
  message: '',
  commentsData: props.comments ? ref(props.comments) : ref([]),
  maxVisibleComments: props.visible,
  loading: false
})

const visibleComments = computed(() => {
  if (!state.commentsData.sort) return []

  const result = [...state.commentsData].sort(function (a, b) {
    return new Date(b.created_at) < new Date(a.created_at) ? 1 : -1
  })

  return result.slice(-state.maxVisibleComments)
})

const session_token = computed(() => {
  return authStore.session_data.token
})

const currentUser = computed(() => {
  return authStore.currentUser
})

const totalComments = computed(() => {
  return state.commentsData.length
})

const showMore = () => {
  state.maxVisibleComments += 5
}

const sendComment = async () => {
  if (!state.message) {
    return
  }
  state.loading = true
  try {
    await createComment(props.endpoint, {
      text: state.message,
      timestamp: new Date()
    })
  } catch (err) {
    state.loading = false
    throw err
  }

  state.loading = false
  state.message = ''
  updateComments()

  emit('comment-submit')
}

const updateComments = async () => {
  const response = await getComments(props.endpoint)

  if (!response) return

  state.commentsData = response
}

onMounted(async () => {
  if (!props.comments) {
    const response = await getComments(props.endpoint)

    if (!response) return

    state.commentsData = response
  }
})
</script>
